import type { FC } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownDivider,
  DropdownHeader,
} from 'flowbite-react';
import {
  HiBookmark,
  HiStar,
  HiTrash,
  HiPlus,
  HiRefresh,
} from 'react-icons/hi';
import type {
  HeatmapSavedFilter,
  SystemPreset,
} from '../../types/filter';

interface SavedFiltersDropdownProps {
  systemPresets: SystemPreset[];
  userPresets: HeatmapSavedFilter[];
  activePresetId?: string | null;
  onSelectPreset: (preset: SystemPreset | HeatmapSavedFilter) => void;
  onSaveNewPreset: () => void;
  onDeletePreset: (presetId: string) => void;
  onSetDefaultPreset: (presetId: string) => void;
  onResetFilters: () => void;
  disabled?: boolean;
}

export const SavedFiltersDropdown: FC<SavedFiltersDropdownProps> = ({
  systemPresets,
  userPresets,
  activePresetId,
  onSelectPreset,
  onSaveNewPreset,
  onDeletePreset,
  onSetDefaultPreset,
  onResetFilters,
  disabled = false,
}) => {
  return (
    <Dropdown
      label={
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <HiBookmark className="h-3.5 w-3.5" />
          <span>Gespeicherte Ansichten</span>
        </span>
      }
      color="light"
      size="xs"
      dismissOnClick={true}
      disabled={disabled}
    >
      {/* 1. System Presets */}
      <DropdownHeader>
        <span className="block text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
          Standard-Vorlagen
        </span>
      </DropdownHeader>

      {systemPresets.map((preset) => {
        const isActive = activePresetId === preset.id;
        return (
          <DropdownItem
            key={preset.id}
            onClick={() => onSelectPreset(preset)}
            className={`flex items-center justify-between text-xs ${
              isActive
                ? 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : ''
            }`}
          >
            <span>{preset.name}</span>
          </DropdownItem>
        );
      })}

      {/* 2. User Presets */}
      {userPresets.length > 0 && (
        <>
          <DropdownDivider />
          <DropdownHeader>
            <span className="block text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
              Meine Vorlagen
            </span>
          </DropdownHeader>

          {userPresets.map((preset) => {
            const isActive = activePresetId === preset.id;
            return (
              <div
                key={preset.id}
                className={`flex items-center justify-between px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-600 ${
                  isActive
                    ? 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelectPreset(preset)}
                  className="flex grow items-center gap-1.5 text-left"
                >
                  <span className="truncate">{preset.name}</span>
                  {preset.is_default && (
                    <span title="Standardansicht">
                      <HiStar className="h-3.5 w-3.5 text-amber-500" />
                    </span>
                  )}
                </button>

                <div className="flex items-center gap-1">
                  {!preset.is_default && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefaultPreset(preset.id);
                      }}
                      className="p-1 text-gray-400 hover:text-amber-500"
                      title="Als Standard festlegen"
                    >
                      <HiStar className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePreset(preset.id);
                    }}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Vorlage löschen"
                  >
                    <HiTrash className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* 3. Action options */}
      <DropdownDivider />
      <DropdownItem
        onClick={onSaveNewPreset}
        className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400"
      >
        <HiPlus className="h-3.5 w-3.5" />
        <span>Als neue Vorlage speichern...</span>
      </DropdownItem>

      <DropdownItem
        onClick={onResetFilters}
        className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400"
      >
        <HiRefresh className="h-3.5 w-3.5" />
        <span>Filter zurücksetzen</span>
      </DropdownItem>
    </Dropdown>
  );
};
